const UNSUPPORTED_SEGMENT_CHARS_REGEXP = /[^a-z0-9_]+/g;

/**
 * Builds a payment icon URL from backend base URL and static icon convention.
 *
 * Returns null when backend base URL is missing or malformed.
 */
function buildPaymentIconUrl(directory: 'drivers' | 'methods', input: string): string | null {
  const backendBaseUrl = process.env.REACT_APP_BACKEND_BASE_URL;

  if (!backendBaseUrl) {
    return null;
  }

  const normalisedSegment = normalisePathSegment(input);

  if (!normalisedSegment) {
    return null;
  }

  try {
    const backendUrl = new URL(backendBaseUrl);

    return `${backendUrl.origin}/images/payment/${directory}/${normalisedSegment}.svg`;
  } catch {
    return null;
  }
}

/**
 * Normalises free-form values into a safe icon path segment.
 *
 * Rules:
 * - trim leading/trailing whitespace
 * - lowercase all characters
 * - replace unsupported characters with underscores
 * - trim leading/trailing underscores
 * - URL-encode the resulting segment
 */
function normalisePathSegment(input: string): string | null {
  const trimmedInput = input.trim().toLowerCase();

  if (!trimmedInput) {
    return null;
  }

  const replacedUnsupportedChars = trimmedInput.replace(UNSUPPORTED_SEGMENT_CHARS_REGEXP, '_').replace(/^_+|_+$/g, '');

  if (!replacedUnsupportedChars) {
    return null;
  }

  return encodeURIComponent(replacedUnsupportedChars);
}

/**
 * Resolves a static driver icon URL from the backend icon convention.
 *
 * Returns null for empty normalised segments or malformed backend configuration.
 */
export function getPaymentDriverIconUrl(driver: string): string | null {
  return buildPaymentIconUrl('drivers', driver);
}

/**
 * Resolves a static payment method icon URL from the backend icon convention.
 *
 * Returns null for empty normalised segments or malformed backend configuration.
 */
export function getPaymentMethodIconUrl(methodCode: string): string | null {
  return buildPaymentIconUrl('methods', methodCode);
}
